'use client'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { usePathLang } from '@/hooks/usePathLang'
import { useTranslation } from '@/i18n/useTranslation'
import { langPrefix } from '@/i18n/translations'
import {
  applyConsentToDocument,
  COOKIE_CONSENT_CHANGE_EVENT,
  getCookieConsent,
  setCookieConsent,
  type CookieConsentChoice,
} from '@/lib/cookieConsent'
import './CookieConsent.css'

export default function CookieConsent() {
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const lang = usePathLang()
  const t = useTranslation(lang)
  const cookiePolicyHref = `${langPrefix(lang)}/legal/cookie-policy`

  useEffect(() => {
    setMounted(true)
    const existing = getCookieConsent()
    if (existing) {
      applyConsentToDocument(existing)
    } else {
      setVisible(true)
    }
  }, [])

  const choose = useCallback((value: CookieConsentChoice) => {
    setCookieConsent(value)
    setVisible(false)
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_CHANGE_EVENT, { detail: value }))
  }, [])

  if (!mounted || !visible) return null

  return (
    <div
      className="cookie-consent"
      role="dialog"
      aria-modal="false"
      aria-live="polite"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
    >
      <div className="cookie-consent__inner">
        <p id="cookie-consent-title" className="cookie-consent__title">
          {t('cookieConsent.title')}
        </p>
        <p id="cookie-consent-desc" className="cookie-consent__body">
          {t('cookieConsent.body')}{' '}
          <Link className="cookie-consent__link" href={cookiePolicyHref}>
            {t('cookieConsent.policyLink')}
          </Link>
        </p>
        <div className="cookie-consent__actions">
          <button
            type="button"
            className="cookie-consent__btn cookie-consent__btn--ghost"
            onClick={() => choose('necessary')}
          >
            {t('cookieConsent.necessaryOnly')}
          </button>
          <button type="button" className="cookie-consent__btn" onClick={() => choose('all')}>
            {t('cookieConsent.acceptAll')}
          </button>
        </div>
      </div>
    </div>
  )
}
